import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import EBib from "@/components/EBib";
import ECertificate from "@/components/ECertificate";
import Uploader from "@/components/Uploader";
import { api, API, formatIDR, formatSeconds, formatPace, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { downloadElementAsPng } from "@/lib/download";
import { CheckCircle, X, Pencil, PlusCircle, Trash, SignOut, Certificate as CertIcon, Barcode, Users, Money, UploadSimple } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [regs, setRegs] = useState([]);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState({});

  const load = async () => {
    try {
      const [meR, statsR, regR, cfgR, evR] = await Promise.all([
        api.get("/auth/admin/me"),
        api.get("/admin/stats"),
        api.get("/admin/registrations"),
        api.get("/config"),
        api.get("/events"),
      ]);
      setMe(meR.data); setStats(statsR.data); setRegs(regR.data); setConfig(cfgR.data); setEvents(evR.data);
    } catch (e) {
      nav("/admin/login");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    await api.post("/auth/admin/logout").catch(() => {});
    localStorage.removeItem("vr_admin_token");
    nav("/admin/login");
  };

  if (!me) return <div className="min-h-screen"><Nav /><div className="p-12 text-white/60">Loading admin...</div></div>;

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#FF3B30] font-display">CONTROL ROOM</div>
            <h1 className="font-display text-4xl mt-2">ADMIN · {me.email}</h1>
          </div>
          <button onClick={logout} className="btn-outline text-xs inline-flex items-center gap-2" data-testid="admin-logout"><SignOut size={16} /> LOGOUT</button>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-1 border-b border-white/10">
          {[
            { k: "overview", l: "OVERVIEW" },
            { k: "events", l: "EVENTS" },
            { k: "payments", l: "PAYMENTS" },
            { k: "results", l: "RESULTS" },
            { k: "finance", l: "KEUANGAN" },
            { k: "settings", l: "SETTINGS" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-xs tracking-widest font-display transition-colors ${tab === t.k ? "text-[#FF3B30] border-b-2 border-[#FF3B30]" : "text-white/60 hover:text-white"}`}
              data-testid={`admin-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab stats={stats} regs={regs} onOpen={setSelected} />}
        {tab === "events" && <EventsTab events={events} reload={load} />}
        {tab === "payments" && (
          <PaymentsTab
            regs={regs.filter((r) => ["pending", "awaiting_proof"].includes(r.payment_status))}
            onOpen={setSelected}
            onChanged={load}
            onRemind={async (r) => {
              try {
                await api.post(`/admin/registrations/${r.id}/remind-payment`);
                toast.success(`Reminder pembayaran terkirim ke ${r.email}`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail));
              }
            }}
            onRemindAll={async () => {
              if (!window.confirm("Kirim email reminder ke SEMUA peserta yang belum upload bukti? Peserta yang sudah diingatkan dalam 24 jam terakhir otomatis dilewati.")) return;
              try {
                const { data } = await api.post("/admin/registrations/remind-all");
                toast.success(`Reminder massal selesai — terkirim: ${data.sent} · dilewati (<24 jam): ${data.skipped_recent} · gagal: ${data.failed}`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail));
              }
            }}
          />
        )}
        {tab === "results" && <ResultsTab regs={regs.filter((r) => r.result_status === "pending" && r.payment_status === "verified")} onOpen={setSelected} />}
        {tab === "finance" && <FinanceTab events={events} />}
        {tab === "settings" && <SettingsTab config={config} reload={load} />}

        {selected && <RegistrationModal reg={selected} config={config} onClose={() => setSelected(null)} onChanged={load} />}
      </div>
    </div>
  );
}

function OverviewTab({ stats, regs, onOpen }) {
  const [sq, setSq] = useState("");
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const [expEv, setExpEv] = useState("");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const q = sq.trim();
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/admin/registrations/search", { params: { q } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]));
    }, 350);
    return () => clearTimeout(t);
  }, [sq]);

  if (!stats) return null;
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard icon={Users} label="TOTAL RUNNERS" value={stats.total_registrations} />
        <StatCard icon={Money} label="REVENUE (IDR)" value={formatIDR(stats.revenue_idr)} />
        <StatCard icon={Barcode} label="PAYMENT PENDING" value={stats.payment_pending} accent />
        <StatCard icon={CertIcon} label="RESULT PENDING" value={stats.result_pending} accent />
      </div>
      <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl">{results !== null ? "HASIL PENCARIAN" : "RECENT REGISTRATIONS"}</h2>
        <input
          value={sq}
          onChange={(e) => setSq(e.target.value)}
          placeholder="Cari nama / email / no BIB..."
          className="tactical-input max-w-[300px]"
          data-testid="overview-search"
        />
      </div>
      {results !== null && (
        <div className="mt-2 text-xs text-white/50" data-testid="overview-search-info">
          {results.length === 0 ? `Tidak ada peserta cocok dengan "${sq.trim()}"` : `${results.length} peserta ditemukan untuk "${sq.trim()}"`}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={expEv} onChange={(e) => setExpEv(e.target.value)} className="tactical-input max-w-[240px]" data-testid="export-event-filter">
          <option value="">Semua Event</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.name}</option>
          ))}
        </select>
        <button onClick={() => window.open(`${API}/admin/export/payments${expEv ? `?event_id=${expEv}` : ""}`, "_blank")} className="btn-outline text-xs" data-testid="export-payments-btn">EXPORT PAYMENT VERIFIED (CSV)</button>
        <button onClick={() => window.open(`${API}/admin/export/results${expEv ? `?event_id=${expEv}` : ""}`, "_blank")} className="btn-outline text-xs" data-testid="export-results-btn">EXPORT RESULTS VERIFIED (CSV)</button>
      </div>
      <RegistrationsTable regs={results !== null ? results : regs.slice(0, 30)} onOpen={onOpen} />
    </div>
  );
}
function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-flat p-5">
      <div className={`flex items-center gap-2 text-[10px] tracking-widest font-display ${accent ? "text-[#FF3B30]" : "text-white/50"}`}>
        <Icon size={14} /> {label}
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}

function ShipmentUploader({ onDone }) {
  const [events, setEvents] = useState([]);
  const [evId, setEvId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);

  const upload = async () => {
    if (!evId) return toast.error("Pilih event terlebih dahulu");
    if (!file) return toast.error("Pilih file Excel/CSV berisi kolom BIB dan RESI");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/events/${evId}/shipments-upload`, fd);
      setResult(data);
      toast.success(`Resi terproses — terkirim: ${data.sent} · dilewati: ${data.skipped} · BIB tak dikenal: ${data.unknown_bibs.length}`);
      if (data.sent > 0) onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-flat p-5" data-testid="shipment-uploader">
      <div className="text-[10px] tracking-widest text-white/50 font-display">UPLOAD RESI PENGIRIMAN (MASSAL)</div>
      <div className="text-xs text-white/50 mt-1">Cukup file Excel/CSV biasa dengan 2 kolom: <span className="text-white/80">BIB</span> dan <span className="text-white/80">RESI</span> (judul kolom bebas: bib/nobib/resi/noresi/tracking). Tidak perlu export dari sistem — pilih event, upload, dan resi otomatis dicocokkan ke peserta berdasarkan nomor BIB. Email "Race Pack Dikirim" terkirim otomatis ke tiap peserta yang cocok; yang sudah terkirim tidak diemail ulang.</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={evId} onChange={(e) => setEvId(e.target.value)} className="tactical-input max-w-[240px]" data-testid="shipment-event-select">
          <option value="">-- Pilih Event --</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.name}</option>
          ))}
        </select>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs text-white/60" data-testid="shipment-file" />
        <button onClick={upload} disabled={busy} className="btn-blaze text-[10px] px-4 py-2" data-testid="shipment-upload-btn">{busy ? "MEMPROSES..." : "UPLOAD RESI"}</button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
        <span className="text-[10px] tracking-widest text-white/50 font-display">LABEL PENGIRIMAN RACE PACK (peserta verified):</span>
        <button onClick={() => evId ? window.open(`${API}/admin/events/${evId}/shipping-labels.pdf?paper=a6`, "_blank") : toast.error("Pilih event terlebih dahulu")} className="btn-outline text-[10px] px-3 py-2" data-testid="labels-a6-btn">LABEL 100×150 (PDF)</button>
        <button onClick={() => evId ? window.open(`${API}/admin/events/${evId}/shipping-labels.pdf?paper=a4`, "_blank") : toast.error("Pilih event terlebih dahulu")} className="btn-outline text-[10px] px-3 py-2" data-testid="labels-a4-btn">LABEL A4 · 4/LEMBAR (PDF)</button>
      </div>
      {result && (
        <div className="mt-3 text-xs text-white/70" data-testid="shipment-summary">
          Terkirim: <span className="text-emerald-400">{result.sent}</span> · Dilewati: {result.skipped} · BIB tak dikenal: <span className="text-amber-400">{result.unknown_bibs.length}{result.unknown_bibs.length > 0 ? ` (${result.unknown_bibs.join(", ")})` : ""}</span>
        </div>
      )}
    </div>
  );
}

function FinanceTab({ events }) {
  const [evId, setEvId] = useState("");
  const [fin, setFin] = useState(null);
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [evSummary, setEvSummary] = useState([]);

  useEffect(() => {
    api.get("/admin/finance/events-summary").then((r) => setEvSummary(r.data || [])).catch(() => {});
  }, []);

  const load = (id) => {
    const target = id ?? evId;
    if (!target) return;
    api.get("/admin/finance", { params: { event_id: target } }).then((r) => setFin(r.data)).catch(() => setFin(null));
  };

  useEffect(() => {
    if (!evId && events.length) { setEvId(events[0].id); load(events[0].id); }
    // eslint-disable-next-line
  }, [events]);

  const add = async () => {
    if (!desc.trim()) return toast.error("Isi keterangan beban");
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) return toast.error("Nominal beban tidak valid");
    setBusy(true);
    try {
      await api.post("/admin/finance/expenses", { event_id: evId, description: desc.trim(), amount_idr: amt, expense_date: date || null });
      toast.success("Beban ditambahkan");
      setDesc(""); setAmount(""); setDate("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus beban ini?")) return;
    try {
      await api.delete(`/admin/finance/expenses/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-8" data-testid="finance-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl">LAPORAN KEUANGAN</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={evId} onChange={(e) => { setEvId(e.target.value); load(e.target.value); }} className="tactical-input max-w-[260px]" data-testid="finance-event-select">
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
          <button onClick={() => window.open(`${API}/admin/finance/export?event_id=${evId}`, "_blank")} className="btn-blaze text-xs px-4 py-2" data-testid="finance-export-btn" disabled={!fin}>DOWNLOAD LAPORAN (CSV)</button>
        </div>
      </div>

      {fin && (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <StatCard icon={Money} label={`PENDAPATAN (${fin.verified_count} PESERTA VERIFIED)`} value={formatIDR(fin.revenue_idr)} />
            <StatCard icon={Barcode} label="TOTAL BEBAN" value={formatIDR(fin.total_expenses_idr)} />
            <StatCard icon={CertIcon} label="SALDO BERSIH" value={formatIDR(fin.net_idr)} accent />
          </div>

          <div className="card-flat p-5 mt-8" data-testid="finance-weekly-chart">
            <div className="text-[10px] tracking-widest text-white/50 font-display">ARUS KAS MINGGUAN — {fin.event_name}</div>
            {(fin.weekly || []).length === 0 ? (
              <div className="text-xs text-white/40 mt-3">Belum ada data mingguan untuk event ini.</div>
            ) : (
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fin.weekly}>
                    <CartesianGrid stroke="#1a1a1a" vertical={false} />
                    <XAxis dataKey="week" tick={{ fill: "#777", fontSize: 10 }} stroke="#333" />
                    <YAxis tick={{ fill: "#777", fontSize: 10 }} stroke="#333" tickFormatter={(v) => `${Math.round(v / 1000)}rb`} />
                    <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ background: "#0A0A0A", border: "1px solid #333", fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" name="Pendapatan" fill="#FF3B30" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="expense" name="Beban" fill="#3f3f46" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="card-flat p-5 mt-8">
            <div className="text-[10px] tracking-widest text-white/50 font-display">TAMBAH BEBAN</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Keterangan (misal: Cetak jersey)" className="tactical-input flex-1 min-w-[200px]" data-testid="finance-exp-desc" />
              <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Nominal (IDR)" inputMode="numeric" className="tactical-input max-w-[160px]" data-testid="finance-exp-amount" />
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="tactical-input max-w-[160px]" data-testid="finance-exp-date" />
              <button onClick={add} disabled={busy} className="btn-blaze text-[10px] px-4 py-2 inline-flex items-center gap-2" data-testid="finance-exp-add"><PlusCircle size={14} /> {busy ? "MENYIMPAN..." : "TAMBAH"}</button>
            </div>
          </div>

          <div className="mt-6 space-y-2" data-testid="finance-expense-list">
            {fin.expenses.length === 0 ? (
              <div className="text-xs text-white/40" data-testid="finance-expense-empty">Belum ada beban tercatat untuk event ini.</div>
            ) : fin.expenses.map((e) => (
              <div key={e.id} className="card-flat px-4 py-3 flex items-center justify-between gap-3" data-testid={`finance-expense-${e.id}`}>
                <div>
                  <div className="text-sm">{e.description}</div>
                  <div className="text-[10px] text-white/40">{e.expense_date || "-"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-mono-num text-sm text-[#FF3B30]">- {formatIDR(e.amount_idr)}</div>
                  <button onClick={() => del(e.id)} className="text-white/40 hover:text-[#FF3B30]" data-testid={`finance-exp-del-${e.id}`}><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {evSummary.length > 0 && (
        <div className="card-flat p-5 mt-8" data-testid="finance-events-chart">
          <div className="text-[10px] tracking-widest text-white/50 font-display">PENDAPATAN & PESERTA PER EVENT</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={evSummary}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="event_name" tick={{ fill: "#777", fontSize: 10 }} stroke="#333" />
                <YAxis yAxisId="rev" tick={{ fill: "#777", fontSize: 10 }} stroke="#333" tickFormatter={(v) => `${Math.round(v / 1000)}rb`} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fill: "#777", fontSize: 10 }} stroke="#333" allowDecimals={false} />
                <Tooltip formatter={(v, name) => (name === "Peserta Verified" ? v : formatIDR(v))} contentStyle={{ background: "#0A0A0A", border: "1px solid #333", fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="rev" dataKey="revenue_idr" name="Pendapatan" fill="#FF3B30" radius={[2, 2, 0, 0]} />
                <Bar yAxisId="cnt" dataKey="verified_count" name="Peserta Verified" fill="#3f3f46" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ regs, onOpen, onRemind, onRemindAll, onChanged }) {
  const pending = regs.filter((r) => r.payment_status === "pending");
  const awaiting = regs.filter((r) => r.payment_status === "awaiting_proof");
  return (
    <div className="mt-8 space-y-8">
      <ShipmentUploader onDone={onChanged} />
      <div data-testid="payments-pending-section">
        <h2 className="font-display text-2xl">MENUNGGU VERIFIKASI ({pending.length})</h2>
        <div className="text-xs text-white/50 mt-1">Sudah upload bukti pembayaran — siap dicek dan diverifikasi.</div>
        <RegistrationsTable regs={pending} onOpen={onOpen} />
      </div>
      <div data-testid="payments-awaiting-section">
        <h2 className="font-display text-2xl text-white/60">BELUM UPLOAD BUKTI ({awaiting.length})</h2>
        <div className="text-xs text-white/50 mt-1">Sudah mendaftar tapi belum upload bukti pembayaran — bisa diingatkan / ditindaklanjuti.</div>
        {awaiting.length > 0 && (
          <button onClick={onRemindAll} className="btn-outline text-[10px] px-3 py-2 mt-3 border-amber-500/50 text-amber-400" data-testid="remind-all-btn">INGATKAN SEMUA ({awaiting.length})</button>
        )}
        <RegistrationsTable regs={awaiting} onOpen={onOpen} onRemind={onRemind} />
      </div>
    </div>
  );
}
function ResultsTab({ regs, onOpen }) {
  return (<div className="mt-8"><h2 className="font-display text-2xl">PENDING RESULTS ({regs.length})</h2><div className="text-xs text-white/50 mt-1 mb-2">Hanya menampilkan hasil lari dari peserta dengan pembayaran terverifikasi.</div><RegistrationsTable regs={regs} onOpen={onOpen} /></div>);
}

function timeAgoId(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "baru saja";
  if (m < 60) return `${m} menit lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  return `${Math.floor(h / 24)} hari lalu`;
}

function RegistrationsTable({ regs, onOpen, onRemind }) {
  if (regs.length === 0) return <div className="card-flat p-10 mt-4 text-center text-white/50">No entries.</div>;
  return (
    <div className="card-flat mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] tracking-widest text-white/50 font-display">
          <tr className="border-b border-white/10">
            <th className="text-left px-4 py-3">BIB</th>
            <th className="text-left px-4 py-3">EVENT</th>
            <th className="text-left px-4 py-3">RUNNER</th>
            <th className="text-left px-4 py-3">CATEGORY</th>
            <th className="text-left px-4 py-3">PAYMENT</th>
            <th className="text-left px-4 py-3">RESULT</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {regs.map((r) => (
            <tr key={r.id} className="border-b border-white/5" data-testid={`admin-row-${r.bib_number}`}>
              <td className="px-4 py-3 font-mono-num">#{r.bib_number}</td>
              <td className="px-4 py-3 text-xs text-[#FF3B30] font-display tracking-widest">{r.event_name}</td>
              <td className="px-4 py-3">{r.full_name}<div className="text-xs text-white/50">{r.email}</div></td>
              <td className="px-4 py-3 font-display tracking-widest">{r.category_name}</td>
              <td className="px-4 py-3"><Badge status={r.payment_status} /></td>
              <td className="px-4 py-3"><Badge status={r.result_status} /></td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {onRemind && r.payment_status === "awaiting_proof" && (
                  <button onClick={() => onRemind(r)} className="btn-outline text-[10px] px-3 py-1 mr-2 border-amber-500/50 text-amber-400" data-testid={`remind-${r.bib_number}`}>INGATKAN</button>
                )}
                <button onClick={() => onOpen(r)} className="btn-outline text-[10px] px-3 py-1" data-testid={`open-${r.bib_number}`}>OPEN</button>
                {onRemind && r.last_payment_reminder_at && (
                  <div className="text-[10px] text-amber-400/70 mt-1" data-testid={`reminded-${r.bib_number}`}>Diingatkan {timeAgoId(r.last_payment_reminder_at)}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Badge({ status }) {
  const map = { pending: "status-pending", verified: "status-verified", rejected: "status-rejected", awaiting_proof: "status-awaiting", not_submitted: "status-awaiting" };
  return <span className={`status-badge ${map[status] || "status-awaiting"}`}>{(status || "").replace("_", " ").toUpperCase()}</span>;
}

function EventCategories({ eventId }) {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const reload = () => api.get(`/categories?event_id=${eventId}`).then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { if (eventId) reload(); /* eslint-disable-next-line */ }, [eventId]);
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest text-white/50 font-display">KATEGORI EVENT INI</div>
        <button onClick={() => setAdding(true)} className="btn-blaze text-xs inline-flex items-center gap-2" data-testid="add-cat"><PlusCircle size={14} /> TAMBAH</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
        {cats.map((c) => (
          <div key={c.id} className="card-flat p-4" data-testid={`cat-${c.name}`}>
            <div className="flex items-start justify-between">
              <div className="font-display text-2xl text-[#FF3B30]">{c.name}</div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} className="text-white/60 hover:text-white p-1" data-testid={`edit-cat-${c.name}`}><Pencil size={14} /></button>
                <button onClick={async () => { if (!window.confirm(`Hapus kategori ${c.name}?`)) return; await api.delete(`/admin/categories/${c.id}`); toast.success("Kategori dihapus"); reload(); }} className="text-white/60 hover:text-[#FF3B30] p-1" data-testid={`del-cat-${c.name}`}><Trash size={14} /></button>
              </div>
            </div>
            <div className="mt-2 text-xs text-white/50 tracking-widest">{c.distance_km} KM · {formatIDR(c.price_idr)}</div>
            <div className="mt-1 text-xs text-white/60">{c.description}</div>
            <div className="mt-2">{c.is_active ? <span className="status-badge status-verified">ACTIVE</span> : <span className="status-badge status-rejected">INACTIVE</span>}</div>
          </div>
        ))}
        {cats.length === 0 && <div className="text-xs text-white/40">Belum ada kategori untuk event ini.</div>}
      </div>
      {(adding || editing) && <CategoryForm cat={editing} eventId={eventId} onClose={() => { setAdding(false); setEditing(null); }} onSaved={() => { setAdding(false); setEditing(null); reload(); }} />}
    </div>
  );
}
function CategoryForm({ cat, eventId, onClose, onSaved }) {
  const [f, setF] = useState({
    name: cat?.name || "",
    distance_km: cat?.distance_km || 0,
    price_idr: cat?.price_idr || 0,
    description: cat?.description || "",
    is_active: cat?.is_active ?? true,
  });
  const save = async () => {
    try {
      if (cat) await api.put(`/admin/categories/${cat.id}`, f);
      else await api.post("/admin/categories", { ...f, event_id: eventId });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} title={cat ? `EDIT ${cat.name}` : "NEW CATEGORY"}>
      <div className="space-y-4" data-testid="cat-form">
        <FormRow label="Name"><input className="tactical-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cat-name" /></FormRow>
        <FormRow label="Distance KM"><input type="number" step="0.01" className="tactical-input" value={f.distance_km} onChange={(e) => setF({ ...f, distance_km: parseFloat(e.target.value) })} data-testid="cat-distance" /></FormRow>
        <FormRow label="Price IDR"><input type="number" className="tactical-input" value={f.price_idr} onChange={(e) => setF({ ...f, price_idr: parseInt(e.target.value) })} data-testid="cat-price" /></FormRow>
        <FormRow label="Description"><textarea className="tactical-input" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormRow>
        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="cat-active" /> Active
        </label>
        <button className="btn-blaze w-full" onClick={save} data-testid="cat-save">SAVE</button>
      </div>
    </Modal>
  );
}

function ShippingLabel({ reg, config }) {
  return (
    <div id={`label-${reg.id}`} className="bg-white text-black border-2 border-black flex flex-col" style={{ width: 400, height: 600, padding: 20 }} data-testid="shipping-label">
      <div className="border-b-2 border-black pb-3">
        <div className="text-[10px] font-bold tracking-[0.2em]">PENGIRIM:</div>
        <div className="mt-1 text-xs leading-snug">
          <div className="font-bold">{config.sender_name || "-"}</div>
          <div>{config.sender_phone || ""}</div>
          <div>{config.sender_address || ""}</div>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-[10px] font-bold tracking-[0.2em]">PENERIMA:</div>
        <div className="mt-1 font-bold text-xl leading-tight uppercase">{reg.full_name}</div>
        <div className="text-sm mt-0.5">{reg.phone}</div>
        <div className="text-sm mt-1 leading-snug">{reg.shipping_address}{reg.destination_city_name ? `, ${reg.destination_city_name}` : ""}</div>
      </div>
      <div className="mt-auto text-center border-t-2 border-black pt-3">
        <div className="font-bold text-4xl tracking-[0.15em]">BIB {reg.bib_number}</div>
        <div className="text-[10px] tracking-[0.2em] mt-1 uppercase">{reg.event_name}</div>
      </div>
    </div>
  );
}

function RegistrationModal({ reg: initReg, config, onClose, onChanged }) {
  const [reg, setReg] = useState(initReg);
  const [note, setNote] = useState("");
  const [resi, setResi] = useState(initReg.tracking_number || "");
  const [tab, setTab] = useState("details");
  const [cert, setCert] = useState(reg.certificate_data || {});
  const ageDisplay = reg.age ? `${reg.age} tahun` : (() => {
    if (!reg.date_of_birth) return "-";
    const a = Math.floor((Date.now() - new Date(reg.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000));
    return a >= 0 ? `${a} tahun` : "-";
  })();

  const action = async (kind, action) => {
    try {
      const { data } = await api.post(`/admin/registrations/${reg.id}/${kind}`, { action, notes: note });
      setReg(data); setNote(""); onChanged();
      const kindLabel = { payment: "Pembayaran", result: "Hasil lari" }[kind] || kind;
      toast.success(`${kindLabel} ${action === "verify" ? "terverifikasi" : "ditolak"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveShipment = async () => {
    if (!resi.trim()) return toast.error("Isi nomor resi terlebih dahulu");
    try {
      const { data } = await api.post(`/admin/registrations/${reg.id}/shipment`, { tracking_number: resi.trim() });
      setReg({ ...reg, shipment_status: "shipped", tracking_number: resi.trim() });
      onChanged();
      toast.success(data.already ? "Resi ini sudah tercatat sebelumnya" : "Resi tersimpan — email 'Race Pack Dikirim' terkirim ke peserta");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveCert = async () => {
    try {
      const { data } = await api.put(`/admin/registrations/${reg.id}/certificate`, cert);
      setReg(data); toast.success("Certificate updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [rcpt, setRcpt] = useState({
    full_name: initReg.full_name || "",
    phone: initReg.phone || "",
    shipping_address: initReg.shipping_address || "",
    destination_city_name: initReg.destination_city_name || initReg.city || "",
  });
  const [savingRcpt, setSavingRcpt] = useState(false);
  const saveRcpt = async () => {
    setSavingRcpt(true);
    try {
      await api.put(`/admin/registrations/${reg.id}/recipient`, rcpt);
      setReg({ ...reg, ...rcpt });
      onChanged();
      toast.success("Data penerima tersimpan — label otomatis mengikuti");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingRcpt(false); }
  };

  return (
    <Modal onClose={onClose} title={`#${reg.bib_number} · ${reg.full_name}`} wide>
      <div className="flex gap-1 border-b border-white/10 mb-6">
        {[{ k: "details", l: "DETAILS" }, { k: "payment", l: "PAYMENT" }, { k: "result", l: "RESULT" }, { k: "cert", l: "CERTIFICATE" }, { k: "label", l: "LABEL" }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 text-xs font-display tracking-widest ${tab === t.k ? "text-[#FF3B30] border-b-2 border-[#FF3B30]" : "text-white/60"}`} data-testid={`modal-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "details" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2 text-sm">
            <Info k="Email" v={reg.email} />
            <Info k="Phone" v={reg.phone} />
            <Info k="Gender" v={reg.gender} />
            <Info k="Usia" v={ageDisplay} />
            <Info k="Ukuran Baju" v={reg.jersey_size || "-"} />
            <Info k="Design Baju" v={reg.jersey_design || "-"} />
            <Info k="City" v={reg.city} />
            <Info k="Alamat Kirim" v={reg.shipping_address || "-"} />
            <Info k="Emergency" v={reg.emergency_contact || "-"} />
            <Info k="Event" v={reg.event_name || "-"} />
            <Info k="Category" v={`${reg.category_name} · ${reg.category_distance_km} KM · ${formatIDR(reg.category_price_idr)}`} />
            {!reg.shipping_courier && <Info k="Wilayah Kirim" v={reg.region_name || "-"} />}
            <Info k="Kurir" v={reg.shipping_courier ? `${reg.shipping_courier.toUpperCase()} ${reg.shipping_service || ""} → ${reg.destination_city_name || "-"}` : "-"} />
            <Info k="Total Bayar" v={reg.unique_code != null ? `${formatIDR(reg.total_transfer_idr ?? (reg.total_price_idr ?? reg.category_price_idr))} (termasuk kode unik ${String(reg.unique_code).padStart(3, "0")})` : formatIDR(reg.total_price_idr ?? reg.category_price_idr)} />
            <button
              onClick={async () => {
                if (!window.confirm(`Hapus registrasi #${reg.bib_number} · ${reg.full_name}?`)) return;
                try {
                  await api.delete(`/admin/registrations/${reg.id}`);
                  toast.success("Registrasi dihapus");
                  onClose();
                  onChanged();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
              className="btn-outline text-xs mt-4 inline-flex items-center gap-2"
              data-testid="reg-delete-btn"
            >
              <Trash size={14} /> DELETE REGISTRATION
            </button>
          </div>
          <div>
            {reg.photo_path && <img src={fileUrl(reg.photo_path)} alt="" className="w-full max-h-80 object-contain border border-[#27272A]" />}
          </div>
        </div>
      )}

      {tab === "payment" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs tracking-widest text-white/50 mb-2 font-display">PROOF OF PAYMENT</div>
            {reg.payment_proof_path ? <img src={fileUrl(reg.payment_proof_path)} alt="proof" className="max-h-96 border border-[#27272A]" /> : <div className="text-white/50">Belum diunggah</div>}
          </div>
          <div className="space-y-4">
            {reg.unique_code != null && (
              <div data-testid="admin-unique-code"><Info k="Total Transfer (dengan Kode Unik)" v={`${formatIDR(reg.total_price_idr ?? reg.category_price_idr)} + ${String(reg.unique_code).padStart(3, "0")} = ${formatIDR(reg.total_transfer_idr ?? (reg.total_price_idr ?? reg.category_price_idr))}`} /></div>
            )}
            <div>Current status: <Badge status={reg.payment_status} /></div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="tactical-input" placeholder="Notes (opsional untuk reject)" rows={2} data-testid="pay-note" />
            <div className="flex gap-2">
              <button className="btn-blaze flex-1 inline-flex items-center justify-center gap-2" onClick={() => action("payment", "verify")} data-testid="pay-verify"><CheckCircle size={16} /> VERIFY</button>
              <button className="btn-outline flex-1 inline-flex items-center justify-center gap-2" onClick={() => action("payment", "reject")} data-testid="pay-reject"><X size={16} /> REJECT</button>
            </div>
            {reg.payment_notes && <div className="text-xs text-white/60">Note: {reg.payment_notes}</div>}
            {reg.payment_status === "verified" && (
              <div className="border-t border-white/10 pt-4">
                <div className="text-[10px] tracking-widest text-white/50 font-display mb-2">RACE PACK / RESI PENGIRIMAN</div>
                {reg.shipment_status === "shipped" && reg.tracking_number && (
                  <div className="text-xs text-emerald-400 mb-2" data-testid="admin-ship-status">DIKIRIM — Resi: {reg.tracking_number}</div>
                )}
                <div className="flex gap-2">
                  <input value={resi} onChange={(e) => setResi(e.target.value)} className="tactical-input flex-1" placeholder="Nomor resi (POS/JNE/dll)" data-testid="admin-ship-input" />
                  <button onClick={saveShipment} className="btn-blaze text-[10px] px-4" data-testid="admin-ship-save">SIMPAN RESI</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "result" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs tracking-widest text-white/50 mb-2 font-display">RESULT SCREENSHOT</div>
            {(() => {
              const atts = (reg.result_attachments && reg.result_attachments.length > 0) ? reg.result_attachments : (reg.result_screenshot_path ? [reg.result_screenshot_path] : []);
              return atts.length > 0 ? (
                <div className="flex flex-wrap gap-3" data-testid="admin-result-attachments">
                  {atts.map((p, i) => (
                    <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer">
                      <img src={fileUrl(p)} alt={`bukti ${i + 1}`} className="h-32 w-32 object-cover border border-[#27272A] hover:border-[#FF3B30] transition-colors" data-testid={`admin-result-att-${i}`} />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-white/50">Belum submit</div>
              );
            })()}
            {reg.result_notes && <div className="mt-3 text-xs text-white/60">Notes: {reg.result_notes}</div>}
            {reg.finisher_photo_path && (
              <div className="mt-4">
                <div className="text-xs tracking-widest text-white/50 mb-2 font-display">FOTO SELFIE FINISHER (tampil di Finisher Wall)</div>
                <a href={fileUrl(reg.finisher_photo_path)} target="_blank" rel="noreferrer">
                  <img src={fileUrl(reg.finisher_photo_path)} alt="selfie finisher" className="h-32 w-32 object-cover border border-[#FF3B30]/50 hover:border-[#FF3B30] transition-colors" data-testid="admin-result-selfie" />
                </a>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <Info k="Finish Time" v={formatSeconds(reg.result_time_seconds)} />
            <Info k="Distance" v={`${reg.result_distance_km || "-"} KM`} />
            <Info k="Pace" v={reg.result_distance_km > 0 && reg.result_time_seconds ? formatPace(reg.result_time_seconds / reg.result_distance_km) : "-"} />
            <div>Status: <Badge status={reg.result_status} /></div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="tactical-input" placeholder="Notes" rows={2} data-testid="res-note" />
            <div className="flex gap-2">
              <button className="btn-blaze flex-1 inline-flex items-center justify-center gap-2" onClick={() => action("result", "verify")} data-testid="res-verify"><CheckCircle size={16} /> VERIFY</button>
              <button className="btn-outline flex-1 inline-flex items-center justify-center gap-2" onClick={() => action("result", "reject")} data-testid="res-reject"><X size={16} /> REJECT</button>
            </div>
          </div>
        </div>
      )}

      {tab === "label" && (
        reg.payment_status !== "verified" ? (
          <div className="text-sm text-amber-400" data-testid="label-locked">Label pengiriman tersedia setelah pembayaran peserta terverifikasi.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="text-[10px] tracking-widest text-white/50 font-display">DATA PENERIMA — bisa diedit jika peserta minta ubah alamat</div>
              <FormRow label="Nama Penerima"><input className="tactical-input" value={rcpt.full_name} onChange={(e) => setRcpt({ ...rcpt, full_name: e.target.value })} data-testid="rcpt-name" /></FormRow>
              <FormRow label="No. HP"><input className="tactical-input" value={rcpt.phone} onChange={(e) => setRcpt({ ...rcpt, phone: e.target.value })} data-testid="rcpt-phone" /></FormRow>
              <FormRow label="Alamat"><textarea className="tactical-input" rows={3} value={rcpt.shipping_address} onChange={(e) => setRcpt({ ...rcpt, shipping_address: e.target.value })} data-testid="rcpt-address" /></FormRow>
              <FormRow label="Kota Tujuan"><input className="tactical-input" value={rcpt.destination_city_name} onChange={(e) => setRcpt({ ...rcpt, destination_city_name: e.target.value })} data-testid="rcpt-city" /></FormRow>
              <div className="flex gap-2">
                <button onClick={saveRcpt} disabled={savingRcpt} className="btn-blaze text-xs px-4 py-2" data-testid="rcpt-save">{savingRcpt ? "MENYIMPAN..." : "SIMPAN DATA PENERIMA"}</button>
                <button onClick={() => downloadElementAsPng(`label-${reg.id}`, `LABEL-${reg.bib_number}.png`)} className="btn-outline text-xs px-4 py-2" data-testid="label-download">DOWNLOAD LABEL (PNG)</button>
              </div>
            </div>
            <div className="overflow-auto">
              <ShippingLabel reg={{ ...reg, ...rcpt }} config={config} />
            </div>
          </div>
        )
      )}

      {tab === "cert" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {[
              ["certificate_title", "Title"],
              ["certificate_subtitle", "Subtitle"],
              ["signature_name", "Signature Name"],
              ["signature_title", "Signature Title"],
              ["footer_text", "Footer Text"],
            ].map(([k, l]) => (
              <FormRow key={k} label={l}>
                <input className="tactical-input" value={cert[k] || ""} onChange={(e) => setCert({ ...cert, [k]: e.target.value })} data-testid={`cert-${k}`} />
              </FormRow>
            ))}
            <div className="flex gap-2">
              <button className="btn-blaze flex-1" onClick={saveCert} data-testid="cert-save">SAVE CERTIFICATE</button>
              <button className="btn-outline" onClick={() => downloadElementAsPng(`ecert-${reg.id}`, `Certificate-${reg.bib_number}.png`, 2700)} data-testid="cert-download">DOWNLOAD</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="scale-50 origin-top-left" style={{ width: "200%" }}>
              <ECertificate registration={{ ...reg, certificate_data: cert }} eventName={reg.event_name || config.event_name} backgroundPath={reg.cert_background_path} sponsorLogos={config.sponsor_logos || []} />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ImageGallery({ label, images = [], onAdd, onRemove, testid, imgClass = "aspect-[4/3] object-cover" }) {
  const [tmp, setTmp] = useState("");
  const handle = (p) => {
    if (p) onAdd(p);
    setTmp("");
  };
  return (
    <div data-testid={`${testid}-gallery`}>
      <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">{label}</div>
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {images.map((img, idx) => (
            <div key={img} className="relative">
              <img src={fileUrl(img)} alt="" className={`w-full border border-[#27272A] ${imgClass}`} />
              <button
                onClick={() => { if (window.confirm("Hapus foto ini?")) onRemove(img); }}
                className="absolute top-1 right-1 bg-black/80 border border-[#FF3B30] text-[#FF3B30] px-2 py-1 text-[10px] font-display tracking-widest"
                data-testid={`${testid}-remove-${idx}`}
                title="Hapus foto ini"
              >
                HAPUS
              </button>
            </div>
          ))}
        </div>
      )}
      <Uploader purpose="general" value={tmp} onUploaded={handle} label={`+ Tambah ${label}`} testid={`${testid}-add`} />
    </div>
  );
}

function SingleImage({ label, path, onSet, onRemove, testid }) {
  const [tmp, setTmp] = useState("");
  const handle = (p) => {
    if (p) onSet(p);
    setTmp("");
  };
  return (
    <div data-testid={`${testid}-single`}>
      <div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">{label}</div>
      {path ? (
        <div className="relative">
          <img src={fileUrl(path)} alt="" className="w-full aspect-[4/3] object-cover border border-[#27272A]" />
          <button
            onClick={() => { if (window.confirm("Hapus gambar ini?")) onRemove(); }}
            className="absolute top-1 right-1 bg-black/80 border border-[#FF3B30] text-[#FF3B30] px-2 py-1 text-[10px] font-display tracking-widest"
            data-testid={`${testid}-remove`}
          >
            HAPUS
          </button>
        </div>
      ) : (
        <Uploader purpose="general" value={tmp} onUploaded={handle} label={`Upload ${label}`} testid={`${testid}-upload`} />
      )}
    </div>
  );
}

function EventsTab({ events, reload }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="mt-8" data-testid="events-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl">EVENTS ({events.length})</h2>
        <button className="btn-blaze text-xs inline-flex items-center gap-2" onClick={() => setEditing({})} data-testid="event-add"><PlusCircle size={16} /> NEW EVENT</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {events.map((ev) => (
          <div key={ev.id} className="card-flat p-6" data-testid={`event-card-${ev.id}`}>
            <div className="flex items-start justify-between">
              <div className="font-display text-2xl tracking-wide">{ev.name}</div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(ev)} className="text-white/60 hover:text-white p-1" data-testid={`event-edit-${ev.id}`}><Pencil size={16} /></button>
                <button onClick={async () => { if (!window.confirm(`Hapus event ${ev.name}?`)) return; try { await api.delete(`/admin/events/${ev.id}`); toast.success("Event dihapus"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} className="text-white/60 hover:text-[#FF3B30] p-1" data-testid={`event-delete-${ev.id}`}><Trash size={16} /></button>
              </div>
            </div>
            {ev.description && <div className="mt-2 text-sm text-white/60 line-clamp-2">{ev.description}</div>}
            <div className="mt-3 flex gap-2 flex-wrap">
              {ev.is_active ? <span className="status-badge status-verified">ACTIVE</span> : <span className="status-badge status-rejected">INACTIVE</span>}
              <span className="status-badge status-awaiting">{(ev.jersey_images || []).length + (ev.medal_images || []).length} FOTO</span>
            </div>
          </div>
        ))}
      </div>
      {editing && <EventForm event={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function DesignListEditor({ designs, onChange }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v || designs.some((d) => d.name === v)) return;
    onChange([...designs, { name: v, image_path: "" }]);
    setVal("");
  };
  return (
    <div>
      <div className="text-[10px] tracking-widest text-white/50 font-display">PILIHAN DESIGN JERSEY (TAMPIL VISUAL DI FORM REGISTRASI)</div>
      <div className="flex gap-2 mt-2">
        <input className="tactical-input flex-1" placeholder="mis. Design A - Lengan Panjang" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="design-input" />
        <button type="button" onClick={add} className="btn-blaze text-xs" data-testid="design-add">TAMBAH</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {designs.map((d) => (
          <div key={d.name} className="border border-white/10 p-3 space-y-2" data-testid={`design-chip-${d.name}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-display tracking-widest">{d.name.toUpperCase()}</span>
              <button type="button" onClick={() => onChange(designs.filter((x) => x.name !== d.name))} className="text-white/50 hover:text-[#FF3B30]" data-testid={`design-del-${d.name}`}>×</button>
            </div>
            <SingleImage label="Foto Design (thumbnail di form registrasi)" path={d.image_path} testid={`design-img-${d.name}`} onSet={(p) => onChange(designs.map((x) => (x.name === d.name ? { ...x, image_path: p } : x)))} onRemove={() => onChange(designs.map((x) => (x.name === d.name ? { ...x, image_path: "" } : x)))} />
          </div>
        ))}
      </div>
      {designs.length === 0 && <div className="text-xs text-white/40 mt-3">Belum ada pilihan design — field tidak tampil di form registrasi.</div>}
    </div>
  );
}

function EventForm({ event, onClose, onSaved }) {
  const [f, setF] = useState({
    name: event.name || "",
    description: event.description || "",
    is_active: event.is_active ?? true,
    jersey_images: event.jersey_images || [],
    jersey_designs: (event.jersey_designs || []).map((d) => (typeof d === "string" ? { name: d, image_path: "" } : d)),
    medal_images: event.medal_images || [],
    bib_background_path: event.bib_background_path || "",
    cert_background_path: event.cert_background_path || "",
    leaderboard_background_path: event.leaderboard_background_path || "",
  });
  const [saving, setSaving] = useState(false);
  const [ratesCount, setRatesCount] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);

  useEffect(() => {
    if (event.id) {
      api.get(`/admin/shipping-rates?event_id=${event.id}`).then((r) => setRatesCount(r.data.count)).catch(() => {});
    }
  }, [event.id]);

  const uploadExcel = async (file) => {
    if (!file || !event.id) return;
    setExcelBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("event_id", event.id);
      const { data } = await api.post("/admin/shipping-rates/upload", fd);
      setRatesCount(data.imported);
      toast.success(`${data.imported} kota berhasil diimport untuk event ini`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setExcelBusy(false);
    }
  };
  const clearRates = async () => {
    if (!window.confirm("Hapus tabel ongkir Excel untuk event ini?")) return;
    try {
      await api.delete(`/admin/shipping-rates?event_id=${event.id}`);
      setRatesCount(0);
      toast.success("Tabel ongkir dihapus");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama event wajib diisi");
    setSaving(true);
    try {
      if (event.id) await api.put(`/admin/events/${event.id}`, f);
      else await api.post("/admin/events", f);
      toast.success("Event tersimpan");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal onClose={onClose} title={event.id ? `EDIT · ${event.name}` : "NEW EVENT"} wide>
      <div className="grid md:grid-cols-2 gap-6" data-testid="event-form">
        <div className="space-y-4">
          <FormRow label="Event Name"><input className="tactical-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="event-name" placeholder="ALLIED RUN VOL. 2" /></FormRow>
          <FormRow label="Description"><textarea className="tactical-input" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="event-desc" /></FormRow>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="event-active" /> Active (peserta bisa daftar)
          </label>
          <button className="btn-blaze" onClick={save} disabled={saving} data-testid="event-save">{saving ? "SAVING..." : "SAVE EVENT"}</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImageGallery label="Foto Jersey" images={f.jersey_images} testid="ev-jersey" onAdd={(p) => setF((prev) => ({ ...prev, jersey_images: [...prev.jersey_images, p] }))} onRemove={(p) => setF((prev) => ({ ...prev, jersey_images: prev.jersey_images.filter((i) => i !== p) }))} />
          <ImageGallery label="Foto Medali" images={f.medal_images} testid="ev-medal" onAdd={(p) => setF((prev) => ({ ...prev, medal_images: [...prev.medal_images, p] }))} onRemove={(p) => setF((prev) => ({ ...prev, medal_images: prev.medal_images.filter((i) => i !== p) }))} />
          <SingleImage label="Background E-BIB" path={f.bib_background_path} testid="ev-bibbg" onSet={(p) => setF((prev) => ({ ...prev, bib_background_path: p }))} onRemove={() => setF((prev) => ({ ...prev, bib_background_path: "" }))} />
          <SingleImage label="Background E-Sertifikat" path={f.cert_background_path} testid="ev-certbg" onSet={(p) => setF((prev) => ({ ...prev, cert_background_path: p }))} onRemove={() => setF((prev) => ({ ...prev, cert_background_path: "" }))} />
          <SingleImage label="Background Leaderboard" path={f.leaderboard_background_path} testid="ev-leaderboardbg" onSet={(p) => setF((prev) => ({ ...prev, leaderboard_background_path: p }))} onRemove={() => setF((prev) => ({ ...prev, leaderboard_background_path: "" }))} />
        </div>
        <div className="mt-4">
          <DesignListEditor designs={f.jersey_designs} onChange={(v) => setF((prev) => ({ ...prev, jersey_designs: v }))} />
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-white/10">
        {event.id ? (
          <EventCategories eventId={event.id} />
        ) : (
          <div>
            <div className="text-[10px] tracking-widest text-white/50 font-display">KATEGORI EVENT INI</div>
            <p className="mt-2 text-xs text-white/40">Simpan event terlebih dahulu — kategori default (5K/10K/21K) dibuat otomatis, lalu bisa kamu ubah di sini.</p>
          </div>
        )}
      </div>
      <div className="mt-6 pt-6 border-t border-white/10">
        <div className="text-[10px] tracking-widest text-white/50 font-display">ONGKOS KIRIM EVENT INI (EXCEL)</div>
        <p className="mt-2 text-xs text-white/50">
          Format bebas: sistem otomatis mendeteksi kolom kota (KANTOR TUJUAN / Kota / Kabupaten / Tujuan / Daerah) dan kolom harga (REGULER / Harga / Tarif / Ongkir / Biaya). Tanpa header pun bisa — cukup 2 kolom: kota + harga. Nama kota boleh tanpa spasi.
        </p>
        {event.id ? (
          <div className="mt-3 grid md:grid-cols-2 gap-4 items-start">
            <div>
              <span className={`status-badge ${ratesCount > 0 ? "status-verified" : "status-awaiting"}`} data-testid="event-shipping-badge">
                {ratesCount > 0 ? `EXCEL ACTIVE · ${ratesCount} KOTA` : "BELUM ADA TABEL ONGKIR"}
              </span>
              {ratesCount === 0 && <p className="mt-2 text-xs text-amber-400">Tanpa tabel Excel, event ini memakai tarif wilayah (flat) dari tab Settings.</p>}
              {ratesCount > 0 && (
                <button className="btn-outline text-xs mt-3" onClick={clearRates} data-testid="event-shipping-clear">HAPUS TABEL</button>
              )}
            </div>
            <label className="flex flex-col items-center justify-center border border-dashed border-[#27272A] hover:border-[#FF3B30] transition-colors p-6 cursor-pointer bg-[#0A0A0A]">
              <UploadSimple size={24} color="#FF3B30" />
              <span className="mt-2 text-sm text-white/70">{excelBusy ? "Mengimport..." : "Upload .xlsx / .csv ongkir event ini"}</span>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="event-shipping-input" onChange={(e) => uploadExcel(e.target.files?.[0])} />
            </label>
          </div>
        ) : (
          <p className="mt-2 text-xs text-amber-400">Simpan event dulu, lalu buka kembali untuk upload Excel ongkir.</p>
        )}
      </div>
      <p className="mt-4 text-xs text-amber-400">Catatan: klik SAVE EVENT untuk menyimpan nama/deskripsi/foto. Upload Excel ongkir tersimpan langsung saat dipilih.</p>
    </Modal>
  );
}

function SettingsTab({ config, reload }) {
  const [qrisPath, setQrisPath] = useState("");
  const [saving, setSaving] = useState(false);
  const [eventName, setEventName] = useState(config.event_name || "");
  const [eventDesc, setEventDesc] = useState(config.event_description || "");
  const [savingContent, setSavingContent] = useState(false);

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/settings", { event_name: eventName, event_description: eventDesc });
      toast.success("Konten event tersimpan");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingContent(false);
    }
  };

  const saveField = async (payload) => {
    try {
      await api.put("/admin/settings", payload);
      toast.success("Tersimpan");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sponsorLogos = config.sponsor_logos || [];
  const [regions, setRegions] = useState(config.regions || []);
  const [savingRegions, setSavingRegions] = useState(false);
  const [bankEnabled, setBankEnabled] = useState(config.bank_transfer_enabled !== false);
  const [bankName, setBankName] = useState(config.bank_name || "");
  const [bankAcc, setBankAcc] = useState(config.bank_account_number || "");
  const [bankHolder, setBankHolder] = useState(config.bank_account_holder || "");
  const [savingBank, setSavingBank] = useState(false);
  const [termsText, setTermsText] = useState(config.terms_text || "");
  const [socIg, setSocIg] = useState(config.social_instagram || "");
  const [socTiktok, setSocTiktok] = useState(config.social_tiktok || "");
  const [socWa, setSocWa] = useState(config.social_whatsapp || "");
  const [socYt, setSocYt] = useState(config.social_youtube || "");
  const [savingInfo, setSavingInfo] = useState(false);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.put("/admin/settings", { terms_text: termsText, social_instagram: socIg, social_tiktok: socTiktok, social_whatsapp: socWa, social_youtube: socYt });
      toast.success("Syarat & ketentuan + social media tersimpan");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingInfo(false); }
  };

  const saveRegions = async () => {
    setSavingRegions(true);
    try {
      await api.put("/admin/settings", { regions });
      toast.success("Wilayah tersimpan");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingRegions(false); }
  };
  const saveBank = async () => {
    setSavingBank(true);
    try {
      await api.put("/admin/settings", { bank_transfer_enabled: bankEnabled, bank_name: bankName, bank_account_number: bankAcc, bank_account_holder: bankHolder });
      toast.success("Bank transfer tersimpan");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingBank(false); }
  };
  const [senderName, setSenderName] = useState(config.sender_name || "");
  const [senderPhone, setSenderPhone] = useState(config.sender_phone || "");
  const [senderAddr, setSenderAddr] = useState(config.sender_address || "");
  const [savingSender, setSavingSender] = useState(false);
  const saveSender = async () => {
    setSavingSender(true);
    try {
      await api.put("/admin/settings", { sender_name: senderName, sender_phone: senderPhone, sender_address: senderAddr });
      toast.success("Data pengirim tersimpan — label pengiriman otomatis memakai data ini");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingSender(false); }
  };
  const updateRegion = (i, key, val) => setRegions((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRegion = () => setRegions((rs) => [...rs, { id: crypto.randomUUID(), name: "", fee_idr: 0 }]);
  const removeRegion = (i) => { if (window.confirm("Hapus wilayah ini?")) setRegions((rs) => rs.filter((_, idx) => idx !== i)); };

  const save = async () => {
    if (!qrisPath) return toast.error("Upload gambar QRIS terlebih dahulu");
    setSaving(true);
    try {
      await api.put("/admin/settings", { qris_image_path: qrisPath });
      toast.success("QRIS tersimpan. Peserta sekarang bisa bayar via QRIS.");
      setQrisPath("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api.put("/admin/settings", { qris_image_path: "" });
      toast.success("QRIS dihapus");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="settings-tab">
      <div className="card-flat p-6">
        <div className="text-[10px] tracking-widest text-white/50 font-display">QRIS MERCHANT</div>
        <p className="mt-2 text-sm text-white/60">
          Upload gambar QRIS merchant Anda. Gambar ini akan tampil di halaman pembayaran peserta sebagai opsi pembayaran selain transfer bank.
        </p>
        <div className="mt-6">
          {config.qris_image_path ? (
            <div>
              <div className="text-xs tracking-widest text-emerald-400 font-display mb-3">CURRENT QRIS ACTIVE</div>
              <img src={fileUrl(config.qris_image_path)} alt="QRIS" className="w-full max-w-[260px] border border-[#27272A] bg-white p-2" data-testid="admin-qris-current" />
              <button onClick={remove} className="btn-outline text-xs mt-4 inline-flex items-center gap-2" data-testid="qris-remove-btn"><Trash size={14} /> REMOVE QRIS</button>
            </div>
          ) : (
            <div className="text-sm text-white/50" data-testid="admin-qris-none">Belum ada QRIS. Upload di bawah ini.</div>
          )}
        </div>
        <div className="mt-6 pt-6 border-t border-white/10">
          <Uploader purpose="general" value={qrisPath} onUploaded={setQrisPath} label="Upload New QRIS Image" testid="qris-upload" />
          <button onClick={save} disabled={saving || !qrisPath} className="btn-blaze mt-4 text-xs" data-testid="qris-save-btn">
            {saving ? "SAVING..." : "SAVE QRIS"}
          </button>
        </div>
      </div>
      <div className="card-flat p-6">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-widest text-white/50 font-display">BANK TRANSFER</div>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input type="checkbox" checked={bankEnabled} onChange={(e) => setBankEnabled(e.target.checked)} data-testid="bank-enabled-toggle" />
            <span className={bankEnabled ? "text-emerald-400 font-display tracking-widest" : "text-[#FF6B6B] font-display tracking-widest"}>{bankEnabled ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>
        <div className="mt-4 space-y-4">
          <FormRow label="Bank Name"><input className="tactical-input" value={bankName} onChange={(e) => setBankName(e.target.value)} data-testid="bank-name-input" /></FormRow>
          <FormRow label="Account Number"><input className="tactical-input" value={bankAcc} onChange={(e) => setBankAcc(e.target.value)} data-testid="bank-account-input" /></FormRow>
          <FormRow label="Account Holder"><input className="tactical-input" value={bankHolder} onChange={(e) => setBankHolder(e.target.value)} data-testid="bank-holder-input" /></FormRow>
          <button className="btn-blaze text-xs" onClick={saveBank} disabled={savingBank} data-testid="bank-save">{savingBank ? "SAVING..." : "SAVE BANK"}</button>
          {!bankEnabled && <div className="text-xs text-amber-400">Bank transfer tidak akan tampil di halaman pembayaran peserta.</div>}
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="text-[10px] tracking-widest text-white/50 font-display">DATA PENGIRIM — LABEL RACE PACK</div>
        <p className="mt-2 text-xs text-white/50">Tercetak di bagian PENGIRIM pada label pengiriman race pack (PNG per peserta & PDF massal di tab PAYMENTS).</p>
        <div className="mt-4 space-y-4">
          <FormRow label="Nama Pengirim"><input className="tactical-input" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="misal: ALLIED RUN — Panitia" data-testid="sender-name-input" /></FormRow>
          <FormRow label="No. HP Pengirim"><input className="tactical-input" type="tel" inputMode="numeric" value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} placeholder="08xxxxxxxxxx" data-testid="sender-phone-input" /></FormRow>
          <FormRow label="Alamat Pengirim"><textarea className="tactical-input" rows={3} value={senderAddr} onChange={(e) => setSenderAddr(e.target.value)} placeholder="Alamat lengkap gudang/sekretariat" data-testid="sender-address-input" /></FormRow>
          <button className="btn-blaze text-xs" onClick={saveSender} disabled={savingSender} data-testid="sender-save">{savingSender ? "SAVING..." : "SAVE PENGIRIM"}</button>
        </div>
      </div>

      <div className="card-flat p-6 md:col-span-2">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-widest text-white/50 font-display">LIVE STATS — PENDAFTAR & FINISHER</div>
            <p className="mt-2 text-sm text-white/60">Tampilkan jumlah pendaftar & finisher secara langsung di halaman utama. Matikan untuk menyembunyikannya dari pengunjung.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer shrink-0">
            <input type="checkbox" checked={config.show_live_stats !== false} onChange={(e) => saveField({ show_live_stats: e.target.checked })} data-testid="live-stats-toggle" />
            <span className={config.show_live_stats !== false ? "text-emerald-400 font-display tracking-widest" : "text-[#FF6B6B] font-display tracking-widest"} data-testid="live-stats-state">{config.show_live_stats !== false ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>
      </div>

      <div className="card-flat p-6 md:col-span-2">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-widest text-white/50 font-display">FINISHER WALL — GALERI FINISHER PUBLIK</div>
            <p className="mt-2 text-sm text-white/60">Tampilkan halaman publik "FINISHERS" berisi galeri semua peserta yang hasil larinya sudah terverifikasi (nama, BIB, kategori, waktu, dan foto selfie finisher jika ada). Matikan untuk menyembunyikan halaman dari pengunjung.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer shrink-0">
            <input type="checkbox" checked={config.show_finisher_wall !== false} onChange={(e) => saveField({ show_finisher_wall: e.target.checked })} data-testid="finisher-wall-toggle" />
            <span className={config.show_finisher_wall !== false ? "text-emerald-400 font-display tracking-widest" : "text-[#FF6B6B] font-display tracking-widest"} data-testid="finisher-wall-state">{config.show_finisher_wall !== false ? "ACTIVE" : "INACTIVE"}</span>
          </label>
        </div>
      </div>

      <div className="card-flat p-6 md:col-span-2">
        <div className="text-[10px] tracking-widest text-white/50 font-display">WILAYAH PENGIRIMAN RACE PACK</div>
        <p className="mt-2 text-sm text-white/60">
          Tarif flat per wilayah ini <span className="text-[#FF3B30]">hanya dipakai sebagai cadangan</span> saat tabel Excel kosong/nonaktif. Selama EXCEL ACTIVE, peserta memakai harga dari tabel Excel.
        </p>
        <div className="mt-6 space-y-3">
          {regions.map((r, i) => (
            <div key={r.id} className="flex gap-3 items-center">
              <input className="tactical-input flex-1 min-w-0" value={r.name} onChange={(e) => updateRegion(i, "name", e.target.value)} placeholder="Nama wilayah" data-testid={`region-name-${i}`} />
              <input type="number" min="0" className="tactical-input shrink-0" style={{ width: "11rem" }} value={r.fee_idr} onChange={(e) => updateRegion(i, "fee_idr", parseInt(e.target.value) || 0)} placeholder="Ongkir IDR" data-testid={`region-fee-${i}`} />
              <button onClick={() => removeRegion(i)} className="text-white/60 hover:text-[#FF3B30] shrink-0" data-testid={`region-remove-${i}`} title="Hapus wilayah"><Trash size={16} /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <button className="btn-outline text-xs inline-flex items-center gap-2" onClick={addRegion} data-testid="region-add"><PlusCircle size={14} /> ADD REGION</button>
          <button className="btn-blaze text-xs" onClick={saveRegions} disabled={savingRegions} data-testid="regions-save">{savingRegions ? "SAVING..." : "SAVE REGIONS"}</button>
        </div>
      </div>

      <div className="card-flat p-6 md:col-span-2">
        <div className="text-[10px] tracking-widest text-white/50 font-display">SITE CONTENT</div>
        <p className="mt-2 text-sm text-white/60">
          Nama & deskripsi brand situs (hero landing page). Konten per-event — foto jersey/medali, background e-BIB & sertifikat — dikelola di tab <span className="text-[#FF3B30]">EVENTS</span>.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div className="space-y-4">
            <FormRow label="Site Name">
              <input className="tactical-input" value={eventName} onChange={(e) => setEventName(e.target.value)} data-testid="event-name-input" placeholder="ALLIED VIRTUAL RUN 2026" />
            </FormRow>
            <FormRow label="Site Description">
              <textarea className="tactical-input" rows={4} value={eventDesc} onChange={(e) => setEventDesc(e.target.value)} data-testid="event-desc-input" placeholder="Kata-kata tentang brand/event Anda..." />
            </FormRow>
            <button className="btn-blaze text-xs" onClick={saveContent} disabled={savingContent} data-testid="event-content-save">
              {savingContent ? "SAVING..." : "SAVE CONTENT"}
            </button>
          </div>
          <div className="space-y-6">
            <div>
              <SingleImage
                label="Foto Ukuran Baju (Size Chart)"
                path={config.size_chart_path}
                testid="size-chart"
                onSet={(p) => saveField({ size_chart_path: p })}
                onRemove={() => saveField({ size_chart_path: "" })}
              />
              <p className="mt-2 text-xs text-white/50">Foto ini tampil tepat di atas pilihan ukuran baju saat peserta registrasi.</p>
            </div>
            <ImageGallery
              label="Logo Sponsor (Global — tampil di semua e-BIB & sertifikat)"
              images={sponsorLogos}
              testid="sponsor"
              imgClass="aspect-[2/1] object-contain bg-white/5 p-2"
              onAdd={(p) => saveField({ sponsor_logos: [...sponsorLogos, p] })}
              onRemove={(p) => saveField({ sponsor_logos: sponsorLogos.filter((i) => i !== p) })}
            />
          </div>
        </div>
      </div>
      <div className="card-flat p-6 md:col-span-2">
        <div className="text-[10px] tracking-widest text-white/50 font-display">SYARAT & KETENTUAN + SOCIAL MEDIA</div>
        <p className="mt-2 text-sm text-white/60">
          Teks S&K tampil sebagai tautan di footer halaman utama (terbuka sebagai popup). Link social media tampil sebagai ikon di footer. Kosongkan yang tidak dipakai.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <FormRow label="Syarat & Ketentuan (teks)">
            <textarea className="tactical-input" rows={8} value={termsText} onChange={(e) => setTermsText(e.target.value)} data-testid="terms-input" placeholder={"Contoh:\n1. Peserta wajib berusia minimal 12 tahun...\n2. Hasil lari wajib diunggah sebelum...\n3. E-sertifikat terbit setelah verifikasi panitia."} />
          </FormRow>
          <div className="space-y-4">
            <FormRow label="Instagram URL"><input className="tactical-input" value={socIg} onChange={(e) => setSocIg(e.target.value)} placeholder="https://instagram.com/alliedrun" data-testid="social-ig-input" /></FormRow>
            <FormRow label="TikTok URL"><input className="tactical-input" value={socTiktok} onChange={(e) => setSocTiktok(e.target.value)} placeholder="https://tiktok.com/@alliedrun" data-testid="social-tiktok-input" /></FormRow>
            <FormRow label="WhatsApp Link"><input className="tactical-input" value={socWa} onChange={(e) => setSocWa(e.target.value)} placeholder="https://wa.me/62812xxxxxxx" data-testid="social-wa-input" /></FormRow>
            <FormRow label="YouTube URL"><input className="tactical-input" value={socYt} onChange={(e) => setSocYt(e.target.value)} placeholder="https://youtube.com/@alliedrun" data-testid="social-yt-input" /></FormRow>
          </div>
        </div>
        <button className="btn-blaze text-xs mt-6" onClick={saveInfo} disabled={savingInfo} data-testid="info-save">{savingInfo ? "SAVING..." : "SAVE S&K + SOCIAL MEDIA"}</button>
      </div>
    </div>
  );
}

function Info({ k, v }) { return (<div><span className="text-white/50 text-xs tracking-widest uppercase">{k}: </span><span className="text-white">{v}</span></div>); }
function FormRow({ label, children }) { return (<label className="block"><div className="text-[10px] tracking-widest text-white/60 uppercase font-display mb-2">{label}</div>{children}</label>); }
function Modal({ children, onClose, title, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`card-flat ${wide ? "max-w-5xl" : "max-w-lg"} w-full mt-16 p-6`} onClick={(e) => e.stopPropagation()} data-testid="modal">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl tracking-widest">{title}</div>
          <button onClick={onClose} className="text-white/60 hover:text-[#FF3B30]" data-testid="modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
